import React from 'react'
import clsx from 'clsx'
import { Back, TickCircle, Trash } from 'iconsax-react'
import { useSWRConfig } from 'swr'
import { useTheme } from '@mui/material'

import Button from '../../atoms/Button2'
import ButtonSecondary from '../../atoms/ButtonSecondary'
import ButtonTertiary from '../../atoms/ButtonTertiary'
import Editor, { isEmpty } from '../../organisms/LexicalEditor'
import PinButton from '@SonarCX/components/atoms/PinButton'

import { deleteNote, postNote, putNote } from '@SonarCX/lib/api/note'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { useNoteVisibility } from '@SonarCX/providers/NoteVisibilityProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import useStyles from './styles'

interface NoteModalProps {
  onClose: () => void
}

const NoteModal: React.FC<NoteModalProps> = ({ onClose }) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { mutate } = useSWRConfig()

  const { user, loaded: envLoaded, env } = useEnvironment()
  const { setAction, setTitle, setV2, setSubtext } = useAreYouSure()
  const { currentNote, setCurrentNote } = useNoteVisibility()
  const [note, setNote] = React.useState<APIAgentNote>(currentNote)
  const [saving, setIsSaving] = React.useState(false)
  const [shouldSave, setShouldSave] = React.useState(false)
  const editing = Boolean(note?.id)
  const hasMarkdown = editing && !note?.content.includes('editor')

  const mutateKey = React.useMemo(() => {
    if (!envLoaded) {
      return 'agentNotes'
    }

    return `agentNotes:${env.name}:${user.id}`
  }, [envLoaded, env, user])

  const classes = useStyles()

  const handleChange = React.useCallback(
    value => {
      const currentNote = { ...note }
      setNote({ ...currentNote, content: value })
      setCurrentNote({ ...currentNote, content: value })
    },
    [note, setCurrentNote]
  )

  const handleDelete = React.useCallback(async () => {
    try {
      await deleteNote(note.id)
      mutate(mutateKey)
      onClose()
      dispatch(AddSuccessAlert('Note deleted'))
    } catch (err) {
      dispatch(AddErrorAlert(err))
    }
  }, [note, mutate, onClose, dispatch, mutateKey])

  const handleDeleteClick = React.useCallback(() => {
    setV2(true)
    setTitle('Delete note?')
    setSubtext('Continuing will erase this note')
    setAction(handleDelete)
  }, [handleDelete, setV2, setTitle, setSubtext, setAction])

  const handleOnBackClick = () => {
    if (!editing && !isEmpty(note.content)) {
      setV2(true)
      setTitle('Are you sure?')
      setSubtext('Continuing will lose changes to this new note')
      setAction(onClose)
      return
    }
    mutate(mutateKey)
    onClose()
  }

  const handleOnBlur = () => {
    if (!shouldSave) setShouldSave(true)
  }

  const handleNoteSave = React.useCallback(
    async (noAlert?: boolean) => {
      try {
        setIsSaving(true)

        if (editing) {
          await putNote(note.id, {
            title: note.title,
            content: note.content
          })
        } else {
          await postNote({
            title: note.title,
            content: note.content
          })
        }
        mutate(mutateKey)

        onClose()
        if (!noAlert) {
          dispatch(AddSuccessAlert('Note saved'))
        }
      } catch (err) {
        dispatch(AddErrorAlert(err))
      } finally {
        setIsSaving(false)
      }
    },
    [note, editing, mutate, onClose, dispatch, mutateKey]
  )

  React.useEffect(() => {
    const handleSave = async () => {
      setShouldSave(false)

      try {
        await putNote(note.id, {
          title: note.title,
          content: note.content
        })
        mutate(mutateKey)
      } catch (err) {
        console.error('Error updating note on blur, ', err)
        dispatch(AddErrorAlert(err))
      }
    }

    if (shouldSave && editing) {
      handleSave()
    }
  }, [shouldSave, editing, note, dispatch, mutate, mutateKey])

  React.useEffect(() => {
    setNote({ ...currentNote })
  }, [currentNote])

  return (
    <div className={classes.root}>
      <div className={classes.dataRoot}>
        <input
          className={classes.titleInput}
          placeholder='Title'
          maxLength={150}
          value={note.title}
          onChange={e => setNote(old => ({ ...old, title: e.target.value }))}
        />
      </div>
      <div className={clsx(classes.dataRoot, classes.editorRoot)}>
        <Editor
          autoFocus
          disableImages
          hasMarkdown={hasMarkdown}
          value={note.content}
          onChange={handleChange}
          onBlur={handleOnBlur}
        />
      </div>
      <div className={classes.btnSection}>
        <div className={classes.btnOptions}>
          {editing && (
            <ButtonTertiary
              onClick={handleDeleteClick}
              className={classes.tertiaryBtn}
            >
              <Trash
                variant='Bold'
                color='white'
                size={theme.size.base}
                className='delete-btn'
              />
            </ButtonTertiary>
          )}
          <ButtonSecondary
            className={classes.btnClass}
            onClick={handleOnBackClick}
            disabled={saving}
          >
            <Back color='black' size={theme.size.base} className='delete-btn' />
          </ButtonSecondary>
          {editing && (
            <PinButton
              id={note.id}
              type='note'
              title={note.title}
              useButton
              extra={note}
              onPin={onClose}
              className={clsx(classes.btnClass, classes.pinBtnClass)}
            />
          )}
        </div>

        <Button
          className={classes.btnClass}
          onClick={() => handleNoteSave()}
          disabled={saving}
        >
          <TickCircle
            variant='Bold'
            color='white'
            size={theme.size.base}
            className='delete-btn'
          />
        </Button>
      </div>
    </div>
  )
}

export default NoteModal
